import { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Chip, 
  CircularProgress, 
  Alert,
  TextField,
  Paper,
  Button,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../context/AuthContext';
import { 
  DataGrid, 
  GridColDef,
  GridToolbar,
  GridFilterModel,
  GridSortModel,
  GridRenderCellParams
} from '@mui/x-data-grid';
import { fetchAllRSVPs, GuestWithRSVP } from '../services/api';
import SummaryCards from '../components/Dashboard/SummaryCards';
import AnalyticsCharts from '../components/Dashboard/AnalyticsCharts';
import QuickFilters from '../components/Dashboard/QuickFilters';
import { 
  calculateAttendanceStats, 
  calculateTransportationStats, 
  calculateRelationshipStats,
  calculateSummaryStats,
  calculateRsvpStatusStats,
  getQuickFilterOptions,
  applyQuickFilter
} from '../utils/dashboardUtils';

function Dashboard() {
  const [rsvps, setRsvps] = useState<GuestWithRSVP[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'lastName',
      sort: 'asc',
    },
  ]);
  const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
  const [quickFilter, setQuickFilter] = useState<string>('all');
  const [filteredRsvps, setFilteredRsvps] = useState<GuestWithRSVP[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [groupMembersMap, setGroupMembersMap] = useState<Map<string, string[]>>(new Map());

  const { isDashboardAuthenticated, authenticateDashboard, dashboardPassphrase } = useAuth();
  const [passphrase, setPassphrase] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);

  // Analytics data
  const attendanceStats = useMemo(() => calculateAttendanceStats(rsvps), [rsvps]);
  const transportationStats = useMemo(() => calculateTransportationStats(rsvps), [rsvps]);
  const relationshipStats = useMemo(() => calculateRelationshipStats(rsvps), [rsvps]);
  const rsvpStatusStats = useMemo(() => calculateRsvpStatusStats(rsvps), [rsvps]);
  const summaryStats = useMemo(() => {
    const pendingRsvps = rsvps.filter(guest => guest.rsvpStatus === 'pending').length;
    return {
      ...calculateSummaryStats(rsvps),
      pendingRsvps
    };
  }, [rsvps]);
  const filterOptions = useMemo(() => getQuickFilterOptions(), []);

  // Create group members map and enhance Plus Ones
  useEffect(() => {
    const map = new Map<string, string[]>();
    
    // First pass: collect all non-Plus One members in each group
    rsvps.forEach(guest => {
      if (guest.firstName !== 'Plus' || guest.lastName !== 'One') {
        const members = map.get(guest.groupId) || [];
        members.push(`${guest.firstName} ${guest.lastName}`);
        map.set(guest.groupId, members);
      }
    });
    
    setGroupMembersMap(map);
  }, [rsvps]);

  // Apply quick filter and search
  useEffect(() => {
    if (rsvps.length > 0) {
      let filtered = applyQuickFilter(rsvps, quickFilter);
      
      // Apply search filter if search term exists
      if (searchTerm.trim()) {
        const lowerSearch = searchTerm.toLowerCase().trim();
        filtered = filtered.filter(guest => {
          const firstName = guest.firstName?.toLowerCase() || '';
          const lastName = guest.lastName?.toLowerCase() || '';
          const fullName = `${firstName} ${lastName}`;
          
          // For Plus Ones, also search in their group member names
          if (guest.firstName === 'Plus' && guest.lastName === 'One') {
            const groupMembers = groupMembersMap.get(guest.groupId) || [];
            const groupMembersStr = groupMembers.join(' ').toLowerCase();
            return firstName.includes(lowerSearch) || 
                   lastName.includes(lowerSearch) || 
                   fullName.includes(lowerSearch) ||
                   groupMembersStr.includes(lowerSearch);
          }
          
          return firstName.includes(lowerSearch) || 
                 lastName.includes(lowerSearch) || 
                 fullName.includes(lowerSearch);
        });
      }
      
      setFilteredRsvps(filtered);
      setFilteredRowCount(filtered.length);
    }
  }, [rsvps, quickFilter, searchTerm, groupMembersMap]);

  useEffect(() => {
    async function loadRSVPs() {
      if (!isDashboardAuthenticated || !dashboardPassphrase) return;
      
      try {
        setLoading(true);
        const data = await fetchAllRSVPs(dashboardPassphrase);
        setRsvps(data);
        setFilteredRsvps(data);
        setFilteredRowCount(data.length);
        setError(null);
      } catch (err) {
        console.error('Failed to load RSVPs:', err);
        setError('Failed to load RSVPs. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    loadRSVPs();
  }, [isDashboardAuthenticated, dashboardPassphrase]);

  const handleAuthenticate = async () => {
    setAuthError(null);
    const success = await authenticateDashboard(passphrase);
    if (!success) {
      setAuthError('Invalid passphrase');
    }
  };

  const columns: GridColDef[] = [
    { 
      field: 'firstName', 
      headerName: 'First Name', 
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as GuestWithRSVP | undefined;
        if (!row) return null;
        
        // Special handling for Plus Ones
        if (row.firstName === 'Plus' && row.lastName === 'One') {
          const groupMembers = groupMembersMap.get(row.groupId) || [];
          const primaryGuest = groupMembers.length > 0 ? groupMembers[0] : 'Unknown';
          return (
            <Box>
              <Typography variant="body2">Plus One</Typography>
              <Typography variant="caption" color="text.secondary">
                (Guest of {primaryGuest})
              </Typography>
            </Box>
          );
        }
        
        return row.firstName;
      }
    },
    { 
      field: 'lastName', 
      headerName: 'Last Name', 
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as GuestWithRSVP | undefined;
        if (!row) return null;
        
        // Don't show "One" for Plus Ones since we're showing it differently
        if (row.firstName === 'Plus' && row.lastName === 'One') {
          return '';
        }
        
        return row.lastName;
      }
    },
    { 
      field: 'relationship', 
      headerName: 'Relationship', 
      flex: 1,
      minWidth: 120
    },
    {
      field: 'rsvpStatus',
      headerName: 'RSVP Status',
      flex: 1,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as GuestWithRSVP | undefined;
        if (!row) return null;
        return (
          <Chip 
            label={row.rsvpStatus === 'responded' ? 'Responded' : 'Pending'} 
            color={row.rsvpStatus === 'responded' ? 'success' : 'warning'}
            size="small"
          />
        );
      }
    },
    {
      field: 'rsvp.welcomeParty.attending',
      headerName: 'Welcome Party',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as GuestWithRSVP | undefined;
        if (!row) return null;
        return (
          <Chip 
            label={row.rsvp?.welcomeParty?.attending ? 'Attending' : 'Not Attending'} 
            color={row.rsvp?.welcomeParty?.attending ? 'success' : 'error'}
            size="small"
          />
        );
      }
    },
    {
      field: 'rsvp.wedding.attending',
      headerName: 'Wedding',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as GuestWithRSVP | undefined;
        if (!row) return null;
        return (
          <Chip 
            label={row.rsvp?.wedding?.attending ? 'Attending' : 'Not Attending'} 
            color={row.rsvp?.wedding?.attending ? 'success' : 'error'}
            size="small"
          />
        );
      }
    },
    {
      field: 'rsvp.transportation.option',
      headerName: 'Transportation',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as GuestWithRSVP | undefined;
        if (!row) return null;
        return (
          <Chip 
            label={row.rsvp?.transportation?.option} 
            color={row.rsvp?.transportation?.option == "bus" ? 'success' : 'error'}
            size="small"
          />
        );
      }
    },
    {
      field: 'rsvp.email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as GuestWithRSVP | undefined;
        if (!row) return null;
        return (
          <Chip 
            label={row.rsvp?.email} 
            size="small"
          />
        );
      }
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          RSVP Dashboard
        </Typography>
        
        {!loading && !error && isDashboardAuthenticated && (
          <Typography variant="subtitle1" align="center" color="text.secondary">
            Showing {filteredRowCount} of {rsvps.length} RSVPs
          </Typography>
        )}
      </Box>

      {!isDashboardAuthenticated && (
        <Box 
          sx={{ 
            maxWidth: 400, 
            mx: 'auto', 
            p: 3, 
            border: '1px solid #e0e0e0', 
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Typography variant="h6" gutterBottom>
            Dashboard Access
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please enter the passphrase to access the RSVP dashboard
          </Typography>
          
          <TextField
            fullWidth
            type="password"
            label="Passphrase"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAuthenticate()}
            sx={{ mb: 2 }}
          />
          
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAuthenticate}
            fullWidth
          >
            Access Dashboard
          </Button>
          
          {authError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {authError}
            </Alert>
          )}
        </Box>
      )}

      {isDashboardAuthenticated && loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {isDashboardAuthenticated && error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      {isDashboardAuthenticated && !loading && rsvps.length === 0 && (
        <Alert severity="info" sx={{ my: 2 }}>
          No RSVPs have been submitted yet.
        </Alert>
      )}

      {isDashboardAuthenticated && !loading && rsvps.length > 0 && (
        <>
          {/* Summary Cards */}
          <SummaryCards 
            totalGuests={summaryStats.totalGuests}
            welcomePartyAttendees={summaryStats.welcomePartyAttendees}
            weddingAttendees={summaryStats.weddingAttendees}
            needingTransportation={summaryStats.needingTransportation}
            pendingRsvps={summaryStats.pendingRsvps}
          />

          {/* Analytics Charts */}
          <AnalyticsCharts 
            welcomePartyData={attendanceStats.welcomeParty}
            weddingData={attendanceStats.wedding}
            transportationData={transportationStats}
            relationshipData={relationshipStats}
            rsvpStatusData={rsvpStatusStats}
          />

          {/* Quick Filters and Search */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <QuickFilters 
              options={filterOptions}
              selectedFilter={quickFilter}
              onFilterChange={setQuickFilter}
            />
            
            <TextField
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                flexGrow: 1,
                maxWidth: 300,
                minWidth: 200
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>

          {/* Data Grid */}
          <Paper elevation={2} sx={{ height: 650, width: '100%', mb: 4 }}>
            <DataGrid
              rows={filteredRsvps}
              columns={columns}
              getRowId={(row) => row.guestId || Math.random().toString()}
              getRowHeight={(params) => {
                const row = params.model as GuestWithRSVP;
                // Make Plus One rows taller to accommodate the two-line display
                if (row && row.firstName === 'Plus' && row.lastName === 'One') {
                  return 70;
                }
                return 52; // default height
              }}
              loading={loading}
              filterModel={filterModel}
              onFilterModelChange={(model) => setFilterModel(model)}
              sortModel={sortModel}
              onSortModelChange={(model) => setSortModel(model)}
              slots={{
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              onStateChange={(params) => {
                if (params.rows.totalRowCount !== undefined) {
                  setFilteredRowCount(params.rows.totalRowCount);
                }
              }}
              initialState={{}}
              pageSizeOptions={[10, 25, 50, 100]}
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          </Paper>
        </>
      )}
    </Container>
  );
}

export default Dashboard;
